import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Account, Transaction } from "@/types/api";
import { ProfileForm } from "@/app/(home)/profile/schema";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { DEFAULT_DATE_FORMAT } from "@/constants";

export const accountApi = createApi({
  reducerPath: "accountApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api/account" }),
  tagTypes: ["Account", "Transactions"],
  endpoints: (builder) => ({
    getAccount: builder.query<Account, void>({
      query: () => {
        return {
          url: "",
          method: "GET",
        };
      },
      providesTags: ["Account"],
    }),
    getSentTransactions: builder.query<Transaction[], string>({
      query: (accountId) => {
        return {
          url: `/${accountId}/transactions`,
          method: "GET",
        };
      },
      providesTags: ["Transactions"],
    }),
    updateAccount: builder.mutation<
      void,
      ProfileForm & {
        accountId?: string;
      }
    >({
      query: (profile) => {
        const formData = new FormData();
        const accountId = profile.accountId;
        delete profile.accountId;

        Object.entries(profile).forEach(([key, value]) => {
          if (!value) {
            return;
          }
          if (value instanceof File) {
            formData.append(key, value);
          } else if (value instanceof Date) {
            formData.append(key, format(value, DEFAULT_DATE_FORMAT));
          } else if (value instanceof Object) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        });

        return {
          url: `/${accountId}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: ["Account"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          toast.error("An error occurred while updating your account.");
        }
      },
    }),
  }),
});

export const {
  useGetAccountQuery,
  useGetSentTransactionsQuery,
  useUpdateAccountMutation,
} = accountApi;
export default accountApi;
